import React, { Component } from 'react';
import classNames from 'classnames';

import { injectIntl, intlShape } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import {
  formatMinimalPriceForDisplay,
  formatPriceForDisplay,
  isNumericMinimalPrice,
} from '../../../components/ListingCard/priceDisplay';
import { getTaskBudgetValue } from '../../../components/ListingCard/taskCardHelpers';
import { ensureListing } from '../../../util/data';
import { isPriceVariationsEnabled } from '../../../util/configHelpers';

import css from './SearchMapPriceLabel.module.css';

/**
 * SearchMapPriceLabel component
 * TODO: change to functional component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {propTypes.listing} props.listing - The listing
 * @param {Function} props.onListingClicked - The function to handle the listing click
 * @param {Object} props.config - The configuration
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
class SearchMapPriceLabel extends Component {
  shouldComponentUpdate(nextProps) {
    const currentListing = ensureListing(this.props.listing);
    const nextListing = ensureListing(nextProps.listing);
    const isSameListing = currentListing.id.uuid === nextListing.id.uuid;
    const hasSamePrice = currentListing.attributes.price === nextListing.attributes.price;
    const hasSameActiveStatus = this.props.isActive === nextProps.isActive;
    const hasSameRefreshToken =
      this.props.mapComponentRefreshToken === nextProps.mapComponentRefreshToken;

    return !(isSameListing && hasSamePrice && hasSameActiveStatus && hasSameRefreshToken);
  }

  render() {
    const {
      className,
      rootClassName,
      listing,
      onListingClicked,
      intl,
      isActive,
      config,
    } = this.props;
    const currentListing = ensureListing(listing);
    const { price, publicData, title } = currentListing.attributes;

    // Create formatted price if currency is known or alternatively show just the unknown currency.
    // §9 display format («180 €»); tasks show their budget («56–89 €», «от 50 €»).
    const isTask = publicData?.listingType === 'tasks';
    // T5 F1: a direct-contact service shows the card's minimal price. A digits-only value is the
    // price («458 €»); a text value only labels the pin («цена в описании»), and its full text is
    // part of the pin's name.
    const minimalPrice =
      publicData?.listingType === 'direct-contact-service'
        ? formatMinimalPriceForDisplay(publicData?.minimalPrice)
        : '';
    const hasTextMinimalPrice = !!minimalPrice && !isNumericMinimalPrice(publicData.minimalPrice);
    const formattedPrice = isTask
      ? getTaskBudgetValue(publicData, intl, config.currency)
      : minimalPrice
      ? minimalPrice
      : price && price.currency === config.currency
      ? formatPriceForDisplay(intl, price)
      : price?.currency
      ? price.currency
      : null;

    const priceValue = hasTextMinimalPrice
      ? intl.formatMessage({ id: 'SearchMapPriceLabel.minimalPriceInDescription' })
      : formattedPrice
      ? intl.formatMessage({ id: 'SearchMapPriceLabel.price' }, { priceValue: formattedPrice })
      : null;

    const validListingTypes = config.listing.listingTypes;
    const foundListingTypeConfig = validListingTypes.find(
      conf => conf.listingType === publicData?.listingType
    );
    const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, foundListingTypeConfig);
    const hasMultiplePriceVariants =
      isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

    const priceMessage = minimalPrice
      ? priceValue
      : hasMultiplePriceVariants
      ? intl.formatMessage({ id: 'SearchMapInfoCard.priceStartingFrom' }, { priceValue })
      : intl.formatMessage({ id: 'SearchMapInfoCard.price' }, { priceValue });

    const classes = classNames(rootClassName || css.root, className);
    const priceLabelClasses = classNames(css.priceLabel, {
      [css.mapLabelActive]: isActive,
      [css.noPriceSetLabel]: !formattedPrice,
    });
    const caretClasses = classNames(css.caret, { [css.caretActive]: isActive });

    // T4b: «{title}, {price}», so a pin is identifiable without the map (title from the listing).
    // Without a price the second part is lowercase and task-specific («бюджет не указан»).
    const noPriceInNameId = isTask
      ? 'SearchMapPriceLabel.screenreader.taskWithoutBudgetInName'
      : 'SearchMapPriceLabel.screenreader.withoutPriceInName';
    const priceInName = hasTextMinimalPrice ? minimalPrice.trim() : priceMessage;
    const ariaLabel = title
      ? intl.formatMessage(
          { id: 'SearchMapPriceLabel.screenreader.mapMarker' },
          { title, price: priceValue ? priceInName : intl.formatMessage({ id: noPriceInNameId }) }
        )
      : priceValue
      ? priceInName
      : intl.formatMessage({ id: 'SearchMapPriceLabel.screenreader.mapMarkerWithoutPrice' });

    return (
      <button
        className={classes}
        onClick={() => onListingClicked(currentListing)}
        aria-label={ariaLabel}
      >
        <div className={css.caretShadow} />
        <div className={priceLabelClasses}>{priceMessage}</div>
        <div className={caretClasses} />
      </button>
    );
  }
}

export default injectIntl(SearchMapPriceLabel);
